/**
 * Modo de resposta em passos URA — texto, FAQ embutido ou agente de IA cadastrado.
 */

import {
  VALIDACAO_RESPOSTA_NONE,
  VALIDACAO_RESPOSTA_NUMERO_RANGE,
  normalizeValidacaoResposta,
  normalizeOptionalNumber,
  validationConfigFromSlot,
  verificarCadastroFromSlot,
} from "./chatbotSlotInputValidation";

export {
  VALIDACAO_RESPOSTA_NONE,
  VALIDACAO_RESPOSTA_NUMERO_RANGE,
  normalizeValidacaoResposta,
  normalizeOptionalNumber,
  validationConfigFromSlot,
  verificarCadastroFromSlot,
};

export const RESPOSTA_MODO_TEXTO = "texto";
export const RESPOSTA_MODO_FAQ_IA = "faq_ia";
export const RESPOSTA_MODO_AGENTE_IA = "agente_ia";

export const ENVIO_RESPOSTA_DIRETO = "direto";
export const ENVIO_RESPOSTA_PERSONALIZADO = "personalizado";

export const MODE_AGENT_PREFIX = "agent:";

export const MAX_CHATBOT_RAG_DOCS = 8;
export const MAX_CHATBOT_RAG_URLS = 6;

export function defaultFaqIaConfig() {
  return {
    source_urls: [],
    documents: [],
    faq_answer_focus: "",
    system_prompt: "",
    model: "",
    temperature: null,
  };
}

export function defaultAgenteIaConfig() {
  return {
    agent_id: "",
    pergunta: "",
    envio_resposta: ENVIO_RESPOSTA_DIRETO,
    resposta_personalizada: "",
  };
}

function parseUrlapiJson(rawUrl) {
  if (rawUrl == null || rawUrl === "") return null;
  const str = String(rawUrl).trim();
  if (!str.startsWith("{") && !str.startsWith("[")) return null;
  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function obrigatorioFromSlot(slot) {
  const fromUrlapi = parseUrlapiJson(slot?.urlapi);
  const raw = slot?.obrigatorio ?? fromUrlapi?.obrigatorio ?? fromUrlapi?.required_slot;
  if (raw === true || raw === 1 || raw === "1") return true;
  const s = String(raw || "")
    .trim()
    .toLowerCase();
  return s === "true" || s === "sim" || s === "yes";
}

export function normalizeFaqIaConfig(raw) {
  const o = raw && typeof raw === "object" ? raw : {};
  return {
    source_urls: Array.isArray(o.source_urls)
      ? o.source_urls.map((u) => String(u || "").trim()).filter(Boolean).slice(0, MAX_CHATBOT_RAG_URLS)
      : [],
    documents: Array.isArray(o.documents)
      ? o.documents
          .map((d) => ({
            url: String(d?.url || d?.file_url || "").trim(),
            file_name: String(d?.file_name || d?.fileName || d?.name || "documento").trim(),
          }))
          .filter((d) => d.url)
          .slice(0, MAX_CHATBOT_RAG_DOCS)
      : [],
    faq_answer_focus: String(o.faq_answer_focus || "").trim(),
    system_prompt: String(o.system_prompt || "").trim(),
    model: String(o.model || "").trim(),
    temperature:
      o.temperature != null && Number.isFinite(Number(o.temperature))
        ? Math.min(1, Math.max(0, Number(o.temperature)))
        : null,
  };
}

export function normalizeAgenteIaConfig(raw) {
  const o = raw && typeof raw === "object" ? raw : {};
  const envio = String(o.envio_resposta || ENVIO_RESPOSTA_DIRETO).trim().toLowerCase();
  return {
    agent_id: String(o.agent_id || o.id || "").trim(),
    pergunta: String(o.pergunta || "").trim(),
    envio_resposta: envio === ENVIO_RESPOSTA_PERSONALIZADO ? ENVIO_RESPOSTA_PERSONALIZADO : ENVIO_RESPOSTA_DIRETO,
    resposta_personalizada: String(o.resposta_personalizada || "").trim(),
  };
}

export function normalizeRespostaModo(raw) {
  const modo = String(raw || RESPOSTA_MODO_TEXTO).trim().toLowerCase();
  if (modo === RESPOSTA_MODO_FAQ_IA) return RESPOSTA_MODO_FAQ_IA;
  if (modo === RESPOSTA_MODO_AGENTE_IA) return RESPOSTA_MODO_AGENTE_IA;
  return RESPOSTA_MODO_TEXTO;
}

export function faqConfigFromSlot(slot) {
  const fromUrlapi = parseUrlapiJson(slot?.urlapi);
  const parsedFromUrl = fromUrlapi
    ? {
        resposta_modo: normalizeRespostaModo(fromUrlapi.resposta_modo),
        faq_ia: normalizeFaqIaConfig(fromUrlapi.faq_ia),
        agente_ia: normalizeAgenteIaConfig(fromUrlapi.agente_ia),
        validacao_resposta: normalizeValidacaoResposta(fromUrlapi.validacao_resposta),
        validacao_numero_min: normalizeOptionalNumber(
          fromUrlapi.validacao_numero_min ?? fromUrlapi.validacao_numero?.min
        ),
        validacao_numero_max: normalizeOptionalNumber(
          fromUrlapi.validacao_numero_max ?? fromUrlapi.validacao_numero?.max
        ),
        verificar_cadastro: verificarCadastroFromSlot({ urlapi: slot?.urlapi }),
        obrigatorio: obrigatorioFromSlot({ urlapi: slot?.urlapi }),
      }
    : null;

  const fromTop = {
    resposta_modo: normalizeRespostaModo(slot?.resposta_modo),
    faq_ia: normalizeFaqIaConfig(slot?.faq_ia),
    agente_ia: normalizeAgenteIaConfig(slot?.agente_ia),
    validacao_resposta: normalizeValidacaoResposta(slot?.validacao_resposta),
    validacao_numero_min: normalizeOptionalNumber(slot?.validacao_numero_min),
    validacao_numero_max: normalizeOptionalNumber(slot?.validacao_numero_max),
    verificar_cadastro: verificarCadastroFromSlot(slot),
    obrigatorio: obrigatorioFromSlot(slot),
  };

  const hasFaqData = (cfg) =>
    cfg &&
    (cfg.resposta_modo === RESPOSTA_MODO_FAQ_IA ||
      cfg.faq_ia.documents.length > 0 ||
      cfg.faq_ia.source_urls.length > 0 ||
      Boolean(cfg.faq_ia.faq_answer_focus));

  const hasAgentData = (cfg) =>
    cfg && (cfg.resposta_modo === RESPOSTA_MODO_AGENTE_IA || Boolean(cfg.agente_ia?.agent_id));

  if (hasAgentData(parsedFromUrl)) return parsedFromUrl;
  if (hasFaqData(parsedFromUrl)) return parsedFromUrl;
  if (hasAgentData(fromTop)) return fromTop;
  if (hasFaqData(fromTop)) return fromTop;
  return parsedFromUrl || fromTop;
}

export function isSlotFaqIaMode(slot) {
  return faqConfigFromSlot(slot).resposta_modo === RESPOSTA_MODO_FAQ_IA;
}

export function isSlotAgenteIaMode(slot) {
  return faqConfigFromSlot(slot).resposta_modo === RESPOSTA_MODO_AGENTE_IA;
}

export function isSlotAiResponseMode(slot) {
  const modo = faqConfigFromSlot(slot).resposta_modo;
  return modo === RESPOSTA_MODO_FAQ_IA || modo === RESPOSTA_MODO_AGENTE_IA;
}

export function modeSelectValue(respostaModo, agenteIa) {
  if (respostaModo === RESPOSTA_MODO_AGENTE_IA && agenteIa?.agent_id) {
    return `${MODE_AGENT_PREFIX}${agenteIa.agent_id}`;
  }
  if (respostaModo === RESPOSTA_MODO_FAQ_IA) return RESPOSTA_MODO_FAQ_IA;
  return RESPOSTA_MODO_TEXTO;
}

export function faqConfigToPayload(
  respostaModo,
  faqIa,
  agenteIa,
  validacaoResposta,
  validacaoNumeroMin,
  validacaoNumeroMax,
  verificarCadastro,
  obrigatorio
) {
  const modo = normalizeRespostaModo(respostaModo);
  const payload = { resposta_modo: modo };
  if (modo === RESPOSTA_MODO_FAQ_IA) {
    payload.faq_ia = normalizeFaqIaConfig(faqIa);
  }
  if (modo === RESPOSTA_MODO_AGENTE_IA) {
    payload.agente_ia = normalizeAgenteIaConfig(agenteIa);
  }
  if (validacaoResposta !== undefined) {
    payload.validacao_resposta = normalizeValidacaoResposta(validacaoResposta);
  }
  if (payload.validacao_resposta === VALIDACAO_RESPOSTA_NUMERO_RANGE) {
    if (validacaoNumeroMin !== undefined) {
      payload.validacao_numero_min = normalizeOptionalNumber(validacaoNumeroMin);
    }
    if (validacaoNumeroMax !== undefined) {
      payload.validacao_numero_max = normalizeOptionalNumber(validacaoNumeroMax);
    }
  }
  if (verificarCadastro !== undefined) {
    if (verificarCadastro) {
      payload.verificar_cadastro = true;
    } else {
      payload.verificar_cadastro = false;
    }
  }
  if (obrigatorio !== undefined) {
    payload.obrigatorio = !!obrigatorio;
  }
  return payload;
}
